import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import RecordsSection from "@/components/RecordsSection";
import HaftantrittView from "@/components/HaftantrittView";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { inhaftColumns, inhaftFields, inhaftDefault } from "@/lib/entities";
import { UserX, Archive } from "lucide-react";

function ArchiveAllButton({ reload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const archiveAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inhaftierungen/archive-all");
      toast.success(`${data.archived} Einträge archiviert`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehlgeschlagen");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button data-testid="inhaft-archive-all-btn" onClick={() => setOpen(true)} variant="outline" className="rounded-sm border-zinc-700 gap-2">
        <Archive className="h-4 w-4" /> Alle archivieren
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zinc-100">Alle Inhaftierungen archivieren?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Alle aktuell angezeigten (aktiven) Einträge werden ins Archiv verschoben.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm bg-transparent border-zinc-700 text-zinc-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="inhaft-archive-all-confirm" onClick={archiveAll} disabled={busy} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white">
              {busy ? "Archiviere…" : "Alle archivieren"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function Inhaftierung() {
  const { user } = useAuth();
  const dn = user?.dienstnummer || "";

  return (
    <div>
      <PageHeader
        eyebrow="SADCR · Vollzug"
        title="Inhaftierung"
        description="Erfassung inhaftierter Straftäter und Haftantritte."
        icon={UserX}
      />
      <div className="p-8">
        <Tabs defaultValue="inhaftierung">
          <TabsList className="bg-[#0C0E12] border border-zinc-800 rounded-sm p-1 h-auto gap-1">
            <TabsTrigger value="inhaftierung" data-testid="inhaft-tab-inhaftierung" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-white text-zinc-400 text-xs uppercase tracking-wide font-medium px-4 py-2">
              Inhaftierung
            </TabsTrigger>
            <TabsTrigger value="haftantritt" data-testid="inhaft-tab-haftantritt" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-white text-zinc-400 text-xs uppercase tracking-wide font-medium px-4 py-2">
              Haftantritt
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inhaftierung" className="mt-6">
            <RecordsSection
              endpoint="/inhaftierungen"
              columns={inhaftColumns}
              fields={inhaftFields}
              makeDefault={inhaftDefault(dn)}
              createLabel="Inhaftierung eintragen"
              emptyLabel="Keine Inhaftierungen erfasst"
              testidPrefix="inhaft"
              renderExtraActions={(reload) => <ArchiveAllButton reload={reload} />}
            />
          </TabsContent>

          <TabsContent value="haftantritt" className="mt-6">
            <HaftantrittView mode="aktiv" dn={dn} testidPrefix="haftantritt" />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
